import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Autocomplete,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/config';
import { IMPORT_BUSINESSES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import {
  buildImportBusinessFormData,
  fetchImportBusinessStatusOptions,
  findImportBusinessOption,
  getImportBusinessName,
  isLeadGenUser,
  isTransferredImportBusinessStatus,
  mapImportBusinessToLeadFormPrefill,
  resolveImportBusinessStatusId,
} from '../../pages/importBusinesses/importBusinessApiHelpers';

const ChangeImportBusinessStatusDialog = ({ open, business, onClose, onSaved }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusOptions, setStatusOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [statusId, setStatusId] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !business) return;

    setStatusId(resolveImportBusinessStatusId(business));
    setDescription('');
    setStatusOptions([]);

    let cancelled = false;
    setOptionsLoading(true);
    fetchImportBusinessStatusOptions()
      .then((list) => {
        if (!cancelled) setStatusOptions(list);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.message || 'Failed to load status options');
        }
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, business]);

  const selectedStatus =
    findImportBusinessOption(statusOptions, statusId, business?.status) ?? null;

  const handleSubmit = async () => {
    const id = business?.id;
    if (!id) return;
    if (!statusId) {
      toast.error('Please select a status');
      return;
    }

    const shouldRedirectToCreateLead =
      isLeadGenUser(user) && isTransferredImportBusinessStatus(statusOptions, statusId);

    setSaving(true);
    try {
      const formData = buildImportBusinessFormData(
        {
          status_id: statusId,
          description: description.trim(),
        },
        { alwaysInclude: ['description'] }
      );
      const { data } = await api.post(IMPORT_BUSINESSES.UPDATE_STATUS(id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data?.message || 'Status updated successfully');
      onSaved?.();
      onClose();

      if (shouldRedirectToCreateLead) {
        navigate('/leads/create', {
          state: {
            importBusinessPrefill: mapImportBusinessToLeadFormPrefill(business, user),
            importBusinessSource: business?.source ?? '',
          },
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Data Status</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Record: {getImportBusinessName(business)}
          </Typography>

          <Autocomplete
            options={statusOptions}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) =>
              String(option?.id) === String(value?.id)
            }
            value={selectedStatus}
            onChange={(_, value) => setStatusId(value?.id ?? '')}
            disabled={saving || optionsLoading}
            loading={optionsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status *"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {optionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter reason or notes for this status change"
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || optionsLoading || !statusId}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saving ? 'Updating...' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeImportBusinessStatusDialog;
